import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/MobileToolTip";
export interface UserDetails {
  username: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string;
}

export interface Ticket {
  id: string;
  number: string;
  reserved_number: string | null;
  status: string;
  created_at: string;
  served_at: string | null;
  eta: number;
  feedback: string | null;
  user_id: string;
  user_details: UserDetails;
}

export interface Counter {
  id: string;
  service: {
    id: string;
    name: string;
  };
  tickets: Ticket[];
}
export interface WaitingListResponse {
  id: string;
  name: string;
  counters: Counter[];
}

import { useTicket } from "@/context/TicketContext";
import { TicketStatus } from "@/services/LiveQueueService";
import { useEffect, useState } from "react";

interface TicketCardProps {
  ticketNumber: string;
  r_ticket_id: number;
  customerName: string;
  serviceId: number;
  userId: number;
  serviceName: string;
  waitTime: string;
  createdAt: string;
  isHighlighted?: TicketStatus; // Optional prop to highlight the first ticket
  served_at: string | null;
  title: string;
  description?: string;
  ticketId: string;
  ticketDate?: string;
  ticketTime?: string;
}

export function TicketCard({
  ticketNumber,
  customerName,
  r_ticket_id,
  serviceName,
  waitTime,
  createdAt,
  serviceId,
  userId,
  isHighlighted,
  served_at,
  description,
  title,
  ticketId,
  ticketDate,
  ticketTime,
}: Readonly<TicketCardProps>) {
  const [waitingMinutes, setWaitingMinutes] = useState<number>(0);
  const [isServing, setIsServing] = useState<boolean>(false); // Track if the ticket is serving

    useEffect(() => {
      const calculateWaitingMinutes = () => {
        const now = new Date();

        const appointmentDateTime = new Date(`${ticketDate}T${ticketTime}`); // local time

        let diffInMs = 0;

        if (isHighlighted === TicketStatus.CHECKED_IN && served_at) {
          setIsServing(true);
          const servedAt = new Date(`${served_at}Z`);
          diffInMs = servedAt.getTime() - appointmentDateTime.getTime();
        } else {
          setIsServing(false);
          diffInMs = now.getTime() - appointmentDateTime.getTime();
        }
        const diffInMinutes = Math.max(0, Math.floor(diffInMs / 60000));
        setWaitingMinutes(diffInMinutes);
      };

      calculateWaitingMinutes();

      const interval = setInterval(() => {
        if (!isServing) {
          calculateWaitingMinutes();
        }
      }, 60000);

      return () => clearInterval(interval);
    }, [ticketDate, ticketTime, served_at, isHighlighted, isServing]);



  const {
    selectTicket,
    // Add other ticket context hooks here
  } = useTicket();
  const getStatusBackground = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.CHECKED_IN:
        return "bg-[#f5bbb7]";
      case TicketStatus.HOLD:
        return "bg-yellow-200";
      default:
        return ""; // Default or PENDING status
    }
  };
  return (
    <button
      onClick={() =>
        selectTicket(
          ticketNumber,
          serviceId,
          r_ticket_id,
          title,
          userId,
          description
        )
      }
      className={`  p-4 rounded-lg border border-gray-200 h-fit ${getStatusBackground(isHighlighted ?? TicketStatus.NEW)}`}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="flex items-center gap-2">
            {ticketNumber.match(/^[Rr]-/) ? (
              <i className="fa-solid fa-calendar-check h-4 w-4"></i>
            ) : (
              <i className="fa-solid fa-ticket h-4 w-4"></i>
            )}
            <span className="text-sm font-medium">
              {ticketNumber.match(/^[Rr]-/)
                ? "Appointment Number:"
                : "Ticket Number:"}
            </span>
            <span className="text-sm">{ticketNumber}</span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 min-w-0">
                  <i className="fa-solid fa-user w-4 h-4"></i>
                  <span className="text-sm font-medium">Customer:</span>
                  <span className="text-sm truncate max-w-[150px]">
                    {customerName}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{customerName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-headset"
            >
              <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" />
              <path d="M21 16v2a4 4 0 0 1-4 4h-5" />
            </svg>
            <span className="text-sm font-medium">Service:</span>
            <span className="text-sm">{serviceName}</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="fa-regular fa-clock"></i>
            <span className="text-sm font-medium">Wait Time:</span>
            {ticketNumber.match(/^[Rr]-/) ? (
              <span className="text-sm"></span>
            ) : (
              <span className="text-sm">
                {waitingMinutes >= 60
                  ? `${Math.floor(waitingMinutes / 60)} hr${waitingMinutes % 60 !== 0 ? ` ${waitingMinutes % 60} min` : ""}`
                  : waitingMinutes > 0
                    ? `${waitingMinutes} min`
                    : null}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <i className="fa-regular fa-clock"></i>
            {ticketNumber.match(/^[Rr]-/) ? (
              <>
                {" "}
                <span className="text-sm font-medium">
                  Expected Arrival Time:
                </span>
                <span className="text-sm">
                  {ticketTime
                    ? new Date(
                        `${new Date().toLocaleDateString()} ${ticketTime}`
                      ).toLocaleTimeString("en-PK", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""}
                </span>
              </>
            ) : (
              <>
                <span className="text-sm font-medium">
                  Ticket Time:
                </span>
                <span className="text-sm">
                  {ticketTime
                    ? new Date(
                        `${new Date().toLocaleDateString()} ${ticketTime}`
                      ).toLocaleTimeString("en-PK", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : ""}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
